// #3382 — shown when the server blocked a SAD/ADR save (REGSYNC.C override gate
// or the #3177 safety net). Before this, the API emitted the SSE event and
// nothing rendered it, so a blocked save looked like a saved one.

export interface SaveBlockedInfo {
  /** sad / adr, or (artefact_locked only) any artefact type the chat can save. */
  artefactType: string;
  kind: 'override_gate' | 'safety_net' | 'locked_section' | 'artefact_locked';
  items: Array<{ handle: string; reason?: string | null }>;
  /** #3680 -- locked_section only. */
  lockedSectionIds?: string[];
  lookupFailed?: boolean;
}

export function parseSaveBlockedEvent(data: {
  type: string;
  artefactType?: string;
  undeclaredCitations?: Array<{ handle: string }>;
  conflicts?: Array<{ handle: string; reason?: string | null }>;
  lockedSectionIds?: string[];
  lookupFailed?: boolean;
}): SaveBlockedInfo | null {
  // #3680 -- a save that hit a locked (handed-off) artefact. Any artefact type the chat saves.
  if (data.type === 'artefact_locked_save_blocked') {
    if (typeof data.artefactType !== 'string' || !/^[a-z_]{2,40}$/.test(data.artefactType)) return null;
    return { artefactType: data.artefactType, kind: 'artefact_locked', items: [] };
  }
  if (data.artefactType !== 'sad' && data.artefactType !== 'adr') return null;
  if (data.type === 'register_override_blocked') {
    return {
      artefactType: data.artefactType,
      kind: 'override_gate',
      items: (data.undeclaredCitations ?? []).map((c) => ({ handle: c.handle })),
    };
  }
  if (data.type === 'locked_section_patch_blocked') {
    return {
      artefactType: data.artefactType,
      kind: 'locked_section',
      items: [],
      lockedSectionIds: (data.lockedSectionIds ?? []).filter((x) => typeof x === 'string'),
      lookupFailed: data.lookupFailed === true,
    };
  }
  if (data.type === 'register_conflict_detected') {
    return {
      artefactType: data.artefactType,
      kind: 'safety_net',
      items: (data.conflicts ?? []).map((c) => ({ handle: c.handle, reason: c.reason })),
    };
  }
  return null;
}

const LOCKED_LABELS: Record<string, string> = {
  prd: 'PRD',
  user_stories: 'user stories',
  sad: 'SAD',
  adr: 'ADR',
  user_journey_map: 'user journey map',
  screen_inventory: 'screen inventory',
  screen_spec: 'screen specs',
  component_catalogue: 'component catalogue',
  design_tokens: 'design tokens',
};

export function SaveBlockedBanner({ info }: { info: SaveBlockedInfo }) {
  const label = info.artefactType.toUpperCase();
  if (info.kind === 'artefact_locked') {
    const name = LOCKED_LABELS[info.artefactType] ?? 'artefact';
    return (
      <div
        role="alert"
        data-testid="save-blocked-banner"
        className="mx-6 mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex-shrink-0"
      >
        <p className="font-semibold">The {name} was not saved — the artefact is locked.</p>
        <p className="mt-1">
          This project&apos;s {name} has been handed off and can no longer be edited from chat. The saved version is unchanged.
          Changes now need a new handoff cycle or an amendment request.
        </p>
      </div>
    );
  }
  if (info.kind === 'locked_section') {
    return (
      <div
        role="alert"
        data-testid="save-blocked-banner"
        className="mx-6 mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex-shrink-0"
      >
        <p className="font-semibold">The {label} patch was not saved — it changes locked text.</p>
        <p className="mt-1">
          {info.lookupFailed
            ? 'The locked sections could not be checked, so the patch was not applied. '
            : <>Locked section(s): <span className="font-mono">{(info.lockedSectionIds ?? []).join(', ') || '(none listed)'}</span>. </>}
          The saved {label} is unchanged. Remove the lock first if the change is intended, or ask the agent to resend the
          patch without touching the locked text.
        </p>
      </div>
    );
  }
  return (
    <div
      role="alert"
      data-testid="save-blocked-banner"
      className="mx-6 mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex-shrink-0"
    >
      <p className="font-semibold">The {label} was not saved — the register check blocked it.</p>
      <p className="mt-1">
        {info.kind === 'override_gate'
          ? `It cites register items that are already resolved or governed without declaring a change: `
          : `It conflicts with a governed register entry: `}
        <span className="font-mono">{info.items.map((i) => i.handle).join(', ') || '(none listed)'}</span>.
        The saved {label} is unchanged. The agent has been told why and will explain on its next reply — ask it to
        re-send with the required OVERRIDES lines.
      </p>
      {info.kind === 'safety_net' &&
        info.items.some((i) => i.reason) && (
          <ul className="mt-1 list-disc pl-5">
            {info.items
              .filter((i) => i.reason)
              .map((i) => (
                <li key={i.handle}>
                  <span className="font-mono">{i.handle}</span>: {i.reason}
                </li>
              ))}
          </ul>
        )}
    </div>
  );
}
